import { Shield, Key, Plus, Trash2, Lock, FileText, Eye, EyeOff, Zap, Copy, Check } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Badge } from '../../ui';
import { useConfigurator } from '../../../hooks/useConfigurator';
import { useState, useMemo } from 'react';
import { clsx } from 'clsx';

export function SecurityStep() {
  const { config, updateSecurity, addVPNPeer, updateVPNPeer, removeVPNPeer } = useConfigurator();
  const { security } = config;
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const toggleKeyVisibility = (peerId: string) => {
    setShowKeys((prev) => ({ ...prev, [peerId]: !prev[peerId] }));
  };

  // Calculate security score (0-100)
  const securityScore = useMemo(() => {
    let score = 30; // Base score for airgapped network
    if (security.firewallDefaultDeny) score += 20;
    if (security.vpnEnabled) score += 15;
    if (security.auditLogging) score += 15;
    if (security.encryptStorage) score += 10;
    if (security.peers.length > 0 && security.peers.every(p => p.publicKey)) score += 10;
    return Math.min(100, score);
  }, [security]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Strong';
    if (score >= 60) return 'Moderate';
    return 'Needs Improvement';
  };

  const applyHighSecurity = () => {
    updateSecurity({
      vpnEnabled: true,
      firewallDefaultDeny: true,
      encryptStorage: true,
      auditLogging: true,
    });
  };

  const isHighSecurityEnabled =
    security.vpnEnabled &&
    security.firewallDefaultDeny &&
    security.encryptStorage &&
    security.auditLogging;

  const copyToClipboard = async (text: string, peerId: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(peerId);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Security Configuration</h2>
          <p className="mt-1 text-gray-500">
            Configure VPN access, encryption, and audit settings. All egress is blocked by default.
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Security Score */}
          <div className="text-center">
            <div className={clsx('text-3xl font-bold', getScoreColor(securityScore))}>
              {securityScore}
            </div>
            <div className="text-xs text-gray-500">{getScoreLabel(securityScore)}</div>
          </div>
          {/* High Security Button */}
          {!isHighSecurityEnabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={applyHighSecurity}
              className="gap-1.5"
            >
              <Zap className="h-4 w-4" />
              Max Security
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* VPN Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key className="h-5 w-5 text-hermit-600" />
                <CardTitle>WireGuard VPN</CardTitle>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={security.vpnEnabled}
                  onChange={(e) => updateSecurity({ vpnEnabled: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-hermit-600 focus:ring-hermit-500"
                />
                <span className="text-sm text-gray-600">Enabled</span>
              </label>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Listen Port"
              type="number"
              value={security.vpnListenPort}
              onChange={(e) =>
                updateSecurity({ vpnListenPort: parseInt(e.target.value) || 51820 })
              }
              disabled={!security.vpnEnabled}
            />
            <Input
              label="VPN Subnet"
              value={security.vpnSubnet}
              onChange={(e) => updateSecurity({ vpnSubnet: e.target.value })}
              hint="IP range for VPN clients"
              disabled={!security.vpnEnabled}
            />
          </CardContent>
        </Card>

        {/* Security Options */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-hermit-600" />
              <CardTitle>Security Options</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={security.firewallDefaultDeny}
                onChange={(e) => updateSecurity({ firewallDefaultDeny: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-hermit-600 focus:ring-hermit-500"
              />
              <div>
                <span className="font-medium text-gray-900">Default Deny Firewall</span>
                <p className="text-sm text-gray-500">
                  Block all traffic except explicitly allowed (recommended)
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={security.encryptStorage}
                onChange={(e) => updateSecurity({ encryptStorage: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-hermit-600 focus:ring-hermit-500"
              />
              <div>
                <span className="font-medium text-gray-900">Encrypt Storage (LUKS)</span>
                <p className="text-sm text-gray-500">
                  Enable full-disk encryption on sensitive nodes
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={security.auditLogging}
                onChange={(e) => updateSecurity({ auditLogging: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-hermit-600 focus:ring-hermit-500"
              />
              <div>
                <span className="font-medium text-gray-900">Audit Logging</span>
                <p className="text-sm text-gray-500">
                  Log all VPN connections and admin actions
                </p>
              </div>
            </label>
          </CardContent>
        </Card>
      </div>

      {/* VPN Peers */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-hermit-600" />
              <CardTitle>Authorized VPN Peers</CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                addVPNPeer({
                  name: `Operator ${security.peers.length + 1}`,
                  publicKey: '',
                  allowedIPs: `10.200.0.${security.peers.length + 2}/32`,
                  enabled: true,
                })
              }
              disabled={!security.vpnEnabled}
              className="gap-1"
            >
              <Plus className="h-4 w-4" />
              Add Peer
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!security.vpnEnabled ? (
            <p className="text-sm text-gray-500">Enable VPN to configure peers</p>
          ) : security.peers.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-gray-200 p-6 text-center">
              <Key className="mx-auto h-10 w-10 text-gray-400" />
              <p className="mt-2 text-gray-500">No VPN peers configured</p>
              <p className="text-sm text-gray-400">
                Add authorized operators who can access the datacenter
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {security.peers.map((peer) => (
                <div
                  key={peer.id}
                  className="flex items-start gap-4 rounded-lg border border-gray-200 p-4"
                >
                  <div className="flex-1 space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Input
                        label="Name"
                        value={peer.name}
                        onChange={(e) =>
                          updateVPNPeer(peer.id, { name: e.target.value })
                        }
                        placeholder="Operator name"
                      />
                      <Input
                        label="Allowed IPs"
                        value={peer.allowedIPs}
                        onChange={(e) =>
                          updateVPNPeer(peer.id, { allowedIPs: e.target.value })
                        }
                        placeholder="10.200.0.2/32"
                      />
                    </div>
                    <div className="relative">
                      <Input
                        label="Public Key"
                        value={peer.publicKey}
                        onChange={(e) =>
                          updateVPNPeer(peer.id, { publicKey: e.target.value })
                        }
                        placeholder="WireGuard public key (base64)"
                        type={showKeys[peer.id] ? 'text' : 'password'}
                        hint="Generate with: wg genkey | wg pubkey"
                      />
                      <div className="absolute right-2 top-7 flex items-center gap-1">
                        {peer.publicKey && (
                          <button
                            type="button"
                            onClick={() => copyToClipboard(peer.publicKey, peer.id)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copy to clipboard"
                          >
                            {copiedKey === peer.id ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleKeyVisibility(peer.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {showKeys[peer.id] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    {peer.description && (
                      <Input
                        label="Description"
                        value={peer.description}
                        onChange={(e) =>
                          updateVPNPeer(peer.id, { description: e.target.value })
                        }
                      />
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={peer.enabled}
                        onChange={(e) =>
                          updateVPNPeer(peer.id, { enabled: e.target.checked })
                        }
                        className="h-4 w-4 rounded border-gray-300 text-hermit-600"
                      />
                      <span className="text-sm text-gray-600">Active</span>
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeVPNPeer(peer.id)}
                      className="text-red-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Summary */}
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-600" />
            <CardTitle className="text-amber-800">Security Posture</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 text-sm sm:grid-cols-2">
            <div className="flex items-center gap-2">
              <Badge variant={security.firewallDefaultDeny ? 'success' : 'warning'}>
                {security.firewallDefaultDeny ? 'Enforced' : 'Disabled'}
              </Badge>
              <span className="text-amber-800">Default deny firewall</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="success">Enforced</Badge>
              <span className="text-amber-800">No egress to internet</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={security.vpnEnabled ? 'success' : 'warning'}>
                {security.vpnEnabled ? 'Active' : 'Disabled'}
              </Badge>
              <span className="text-amber-800">VPN-only remote access</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={security.auditLogging ? 'success' : 'warning'}>
                {security.auditLogging ? 'Enabled' : 'Disabled'}
              </Badge>
              <span className="text-amber-800">Audit logging</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={security.encryptStorage ? 'success' : 'default'}>
                {security.encryptStorage ? 'Enabled' : 'Optional'}
              </Badge>
              <span className="text-amber-800">Storage encryption</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={security.peers.length > 0 ? 'success' : 'warning'}>
                {security.peers.filter((p) => p.enabled).length} peers
              </Badge>
              <span className="text-amber-800">Authorized VPN peers</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
